import { useStore } from "jotai";
import type React from "react";
import { forwardRef, useEffect, useMemo, useState } from "react";
import type { WorldState } from "#scripts/vmc/WorldState";
import { DevEntityDetails } from "./DevEntityDetails";
import { DevPanel, type TrackedEntity, devPanelActiveTabAtom, isDevPanelMinimizedAtom } from "./DevPanel";
import { DevUIDStyles } from "./DevUIDStyles";
import { devHoverDetailsAtom, devHoverPositionAtom, useDevHover } from "./useDevHover";
import type { DevHoverDetails } from "./useDevHover";
import { useStoredValue } from "./useStoredValue";
import { ProvideWorldState } from "./world-state-context";

function AtomValue<T>({ atom, children }: { atom: any; children: (value: T) => React.ReactNode }) {
  const store = useStore();
  const [value, setValue] = useState<T>(() => store.get(atom));

  useEffect(() => {
    return store.sub(atom, () => {
      setValue(store.get(atom));
    });
  }, [store, atom]);

  return <>{children(value)}</>;
}

export const ProvideWorldStateDev = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    world: WorldState;
  }
>(({ children, className, world, ...props }, ref) => {
  const { handleMouseMove, handleClick, clearHover } = useDevHover();
  const store = useStore();
  const [trackedEntitiesRaw, setTrackedEntities] = useStoredValue<Pick<TrackedEntity, "uid" | "detailed">[]>(
    "local",
    `trackedEntities-${typeof location !== "undefined" ? location.pathname : "demo"}`,
    () => [],
  );

  const trackedEntities = useMemo(() => {
    return trackedEntitiesRaw.map((entity) => ({
      ...entity,
      close: () => setTrackedEntities((prev) => prev.filter((e) => e.uid !== entity.uid)),
    }));
  }, [trackedEntitiesRaw, setTrackedEntities]);

  useEffect(() => {
    return store.sub(devHoverDetailsAtom, () => {
      const details = store.get(devHoverDetailsAtom);
      if (details?.isClick) {
        store.set(isDevPanelMinimizedAtom, false);
        store.set(devPanelActiveTabAtom, "entities");
        setTrackedEntities((prev) => {
          return [
            {
              uid: details.uid,
              detailed: details.alt,
            },
            ...prev.filter((e) => e.uid !== details.uid),
          ];
        });
      }
    });
  }, [store, setTrackedEntities]);

  return (
    <ProvideWorldState value={world}>
      <div
        ref={ref}
        data-loc="phosphor-blog/scripts/world-state-demo/ProvideWorldStateDev.tsx:70:9" data-name="div" className={`relative ${className || ""}`}
        onPointerDown={handleClick}
        onMouseMove={handleMouseMove}
        onMouseLeave={clearHover}
      >
        {children}
        <DevUIDStyles />
        <AtomValue atom={devHoverDetailsAtom}>
          {(hoverDetails: DevHoverDetails | null) =>
            world && hoverDetails && !hoverDetails.isClick && !hoverDetails.noPopup ? (
              <DevEntityDetails
                data-loc="phosphor-blog/scripts/world-state-demo/ProvideWorldStateDev.tsx:81:17" data-name="DevEntityDetails" className="fixed z-[1002] max-w-md"
                uid={hoverDetails.uid}
                detailed={hoverDetails.alt}
                ref={(element) => {
                  if (element) {
                    store.sub(devHoverPositionAtom, () => {
                      const position = store.get(devHoverPositionAtom);
                      element.style.left = position.pageX + "px";
                      element.style.top = position.pageY + "px";
                    });
                  }
                }}
              />
            ) : null
          }
        </AtomValue>

        <DevPanel trackedEntities={trackedEntities} onClearEntities={() => setTrackedEntities([])} />
      </div>
    </ProvideWorldState>
  );
});
ProvideWorldStateDev.displayName = "ProvideWorldStateDev";
